import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import { KEY_RIGHT, KEY_LEFT } from 'keycode-js';
import React, { useRef, useState, useCallback, useEffect, createContext } from 'react';
import Measure from 'react-measure';
import Divider from '../Divider.js';
import { TabsContent } from './TabsContent.js';
import { TabsItem } from './TabsItem.js';
import TabsOverflowDropdown from './TabsOverflowDropdown.js';

var _excluded = ["child", "updateTabDimensions", "hiddenItems", "handleKeyDown"],
  _excluded2 = ["size", "justified", "className", "activeTab", "defaultTab", "onTabSelect", "disableAdaptive", "children"];
var TabsContext = /*#__PURE__*/createContext();
var isActive = function isActive(activeItem, items) {
  return activeItem && items && items.filter(function (item) {
    return item.name === activeItem;
  }).length > 0;
};
var MeasuredTab = function MeasuredTab(_ref) {
  var child = _ref.child,
    updateTabDimensions = _ref.updateTabDimensions,
    hiddenItems = _ref.hiddenItems,
    handleKeyDown = _ref.handleKeyDown,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var tabRef = useRef();
  useEffect(function () {
    var currentWidth = tabRef.current.clientWidth; //individual tab width
    var tabName = child.props.name || null;
    var label = child.props.children;
    updateTabDimensions(tabName, currentWidth, label, child);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTabDimensions, child.props.name]);
  if (child && !hiddenItems[child.props.name]) {
    return /*#__PURE__*/React.createElement("li", _extends({
      className: "C-Tabs__Item",
      ref: tabRef
    }, otherProps, {
      role: "presentation"
    }), /*#__PURE__*/React.createElement(child.type, _extends({}, child.props, {
      onKeyDown: function onKeyDown(e) {
        return handleKeyDown(e, child.props.name);
      }
    })));
  } else {
    return '';
  }
};
MeasuredTab.displayName = 'MeasuredTab';

// parent renderer
var Tabs = function Tabs(props) {
  var _classnames, _tabDimensions$;
  var size = props.size,
    justified = props.justified,
    className = props.className,
    activeTab = props.activeTab,
    defaultTab = props.defaultTab,
    _onTabSelect = props.onTabSelect,
    _props$disableAdaptiv = props.disableAdaptive,
    disableAdaptive = _props$disableAdaptiv === void 0 ? false : _props$disableAdaptiv,
    propsChildren = props.children,
    otherProps = _objectWithoutProperties(props, _excluded2);
  // ref for measuring overflow dropdown menu
  var dropdownRef = useRef();

  // Return an array of children
  var children = React.Children.toArray(propsChildren);
  var finalClasses = classnames('C-Tabs', (_classnames = {
    '--disable-adaptive': disableAdaptive
  }, _defineProperty(_classnames, "--size-".concat(size), size), _defineProperty(_classnames, '--space-between', justified), _classnames), className);
  var tabCache = [];
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    tabRowDimensions = _useState2[0],
    setTabRowDimensions = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    tabDimensions = _useState4[0],
    setTabDimensions = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    dropdownWidth = _useState6[0],
    setDropdownWidth = _useState6[1];
  var _useState7 = useState({}),
    _useState8 = _slicedToArray(_useState7, 2),
    hiddenItems = _useState8[0],
    setHiddenItems = _useState8[1];

  /** The edge case specifically happens when Mearsure bounds returns that there is enough
   room for the first tab only, but when we add the "More" dropdown tab and the total width becomes
   just large enough that Mearsure recalculates tries to fit both tab headers. This causes the constent
   flickering. The solution is to allow the tab header to inherit the width from the size of 
   the text within and override the Mearsure bounds calcuated width. 
  **/
  var edgeCase = ((_tabDimensions$ = tabDimensions[0]) === null || _tabDimensions$ === void 0 ? void 0 : _tabDimensions$.width) / tabRowDimensions.width > 0.5;
  var finalClassesItem = classnames('C-Tabs__Item', {
    '--size-wide': edgeCase
    // Only want to apply the inherit width when the first Tab and More dropdown are larger
    // then the width of whatever Grid or Container it is inclosed in.
  }, className);
  var updateTabDimensions = useCallback(function (tabName, tabWidth, tabLabel, child) {
    var localTabDimensions = tabDimensions;
    localTabDimensions.push({
      active: child.props.active ? true : false,
      label: tabLabel,
      name: tabName,
      otherProps: child ? child.props : [],
      width: tabWidth
    });
    setTabDimensions(localTabDimensions);
  }, [tabDimensions]);
  useEffect(function () {
    // Create a list of tab names
    children.map(function (child) {
      if (child) {
        var tabName = child.props.name || null;
        if (!tabCache.includes(tabName)) tabCache.push(tabName);
      }
    });
  }, [propsChildren]);
  useEffect(function () {
    if (Object.keys(hiddenItems).length) {
      setDropdownWidth(dropdownRef.current.clientWidth);
    } else {
      setDropdownWidth(0);
    }
  }, [defaultTab, activeTab, hiddenItems]);
  useEffect(function () {
    if (!disableAdaptive) {
      var localHiddenItems = {};
      var consumedTabWidth = 0;
      var currentMaxWidth = Math.floor(tabRowDimensions ? tabRowDimensions.width : 0);
      var lastTabWidth = tabDimensions[tabDimensions.length - 1] ? tabDimensions[tabDimensions.length - 1].width : 0;
      var rightMostChildWidth = dropdownWidth === 0 ? lastTabWidth : dropdownWidth;
      tabDimensions.forEach(function (tab, index) {
        consumedTabWidth += tab.width;
        if (consumedTabWidth + rightMostChildWidth + 2 > currentMaxWidth) {
          localHiddenItems[tab.name] = {
            active: tab.active,
            index: index,
            label: tab.label,
            name: tab.name,
            otherProps: tab.otherProps
          };
        }
      });
      setHiddenItems(localHiddenItems);
    }
  }, [tabDimensions, tabRowDimensions, dropdownWidth, disableAdaptive]);
  var setTabRowWidth = function setTabRowWidth(_ref2) {
    var bounds = _ref2.bounds;
    if (!disableAdaptive) setTabRowDimensions(bounds);
  };
  var handleKeyDown = function handleKeyDown(event, name) {
    if (event.keyCode === KEY_RIGHT || event.keyCode === KEY_LEFT) {
      var currentTabIndex = tabCache.indexOf(name);
      if (currentTabIndex === -1) {
        console.error('Hey Canvas User! The Tabs.Item component must have a name in order to be selected. Please add name="string" to each tab.');
        return false;
      }
      var tabName = null;

      // Move Right
      if (event.keyCode === KEY_RIGHT) {
        if (tabCache.length === currentTabIndex + 1) {
          // We're at the end of the tab list, go to the beginning
          tabName = tabCache[0];
        } else {
          // Advance to the next tab to the "right"
          tabName = tabCache[currentTabIndex + 1];
        }

        // Move left
      } else if (event.keyCode === KEY_LEFT) {
        if (currentTabIndex === 0) {
          // If we're at the beginning of the list, go to the end
          tabName = tabCache[tabCache.length - 1];
        } else {
          // Advance to the next tab to the "right"
          tabName = tabCache[currentTabIndex - 1];
        }
      }

      // focus on left/right tab
      if (tabName !== null) document.getElementById("".concat(tabName, "-tab-control")).focus();
    }
  };
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: {
      activeTab: activeTab || defaultTab,
      onTabSelect: function onTabSelect(name) {
        return function (event) {
          return _onTabSelect && _onTabSelect(event, {
            name: name
          });
        };
      }
    }
  }, /*#__PURE__*/React.createElement(Measure, {
    bounds: !disableAdaptive ? true : false,
    onResize: setTabRowWidth
  }, function (_ref3) {
    var measureRef = _ref3.measureRef;
    return /*#__PURE__*/React.createElement("ul", _extends({
      className: finalClasses
    }, otherProps, {
      role: "tablist",
      ref: !disableAdaptive ? measureRef : null
    }), children && children.map(function (child) {
      if (child.type.displayName === 'Tabs.Item') {
        return /*#__PURE__*/React.createElement(MeasuredTab, {
          child: child,
          key: "".concat(child.props.name, "_measured"),
          updateTabDimensions: updateTabDimensions,
          hiddenItems: hiddenItems,
          handleKeyDown: handleKeyDown
        });
      }
    }), Object.keys(hiddenItems).length > 0 && /*#__PURE__*/React.createElement("li", {
      className: finalClassesItem,
      role: "presentation",
      ref: dropdownRef,
      key: "overflowDropdown"
    }, /*#__PURE__*/React.createElement(TabsOverflowDropdown, {
      items: hiddenItems,
      activeItem: activeTab || defaultTab,
      onSelect: _onTabSelect,
      isActive: isActive
    })));
  }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite"
  }, children.map(function (child, i) {
    if (child && child.type.displayName === 'Tabs.Content') {
      return /*#__PURE__*/React.createElement(child.type, _extends({}, child.props, {
        key: i
      }));
    }
  })));
};
Tabs.displayName = 'Tabs';
Tabs.Item = TabsItem;
Tabs.Content = TabsContent;

export { TabsContext, Tabs as default };
